import React from 'react';
import { Transaction } from '../../types';
import { useCurrency } from '../../context/CurrencyContext';
import { formatAmount, formatDateShort, formatExactPrice } from '../../utils/formatters';
import { CoinIcon } from './CoinIcon';
import { RowActions } from './RowActions';
import { TotalCell, TypePill, WalletCell, relativeTime } from './txnHelpers';
import { TxnRow } from './TxnRow';
import { usePortfolioContext } from '../../context/PortfolioContext';
import { describeDeletionImpact } from '../../utils/walletTimeline';
import { txPriceInDisplay } from '../../utils/displayAmount';

interface Props {
  rows: Transaction[];               // already paged, newest first
  imageMap: Record<string, string>;
  onEdit: (tx: Transaction) => void;
  onDelete: (id: string) => void;
}

const TH: React.CSSProperties = {
  fontSize: 10,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--color-label)',
  fontWeight: 500,
};

function monthLabel(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

/**
 * Tag each row with the month heading that should precede it, or null.
 *
 * Computed once and consumed by BOTH layouts below, so the phone list and the
 * desktop table group identically — two walks with their own running "last month"
 * variable is exactly the kind of thing that quietly diverges.
 */
function withMonthDividers(rows: Transaction[]): { tx: Transaction; divider: string | null }[] {
  return rows.map((tx, i) => {
    const m = monthLabel(tx.date);
    return { tx, divider: i === 0 || m !== monthLabel(rows[i - 1].date) ? m : null };
  });
}

/** The month heading shown between groups. Shared by both layouts. */
function MonthDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3">
      <span
        className="uppercase font-semibold whitespace-nowrap"
        style={{ fontSize: 10, letterSpacing: '0.1em', color: '#b89968' }}
      >
        {label}
      </span>
      <span className="flex-1 h-px" style={{ background: 'rgb(var(--overlay) / 0.06)' }} />
    </div>
  );
}

export function AllTransactionsTable({ rows, imageMap, onEdit, onDelete }: Props) {
  // `fmt` is no longer needed here: totals render through <TotalCell>.
  const { rate, currency } = useCurrency();
  const { transactions: allTxs } = usePortfolioContext();

  if (rows.length === 0) {
    return (
      <div className="py-12 text-center text-sm" style={{ color: 'var(--color-sub)' }}>
        No transactions to show
      </div>
    );
  }

  const grouped = withMonthDividers(rows);

  return (
    <>
      {/* ── Below md: stacked cards ────────────────────────────────────────────
          The table is eight columns wide and needs 760px; a 375px phone could
          only ever see a slice of it, with the left columns cut off and the page
          scrolling sideways. So below md the table is not rendered at all (not
          merely scrolled) and the same rows render as cards, using the SAME row
          component as the per-coin Buys/Sells lists.

          `showAsset` is what makes it work here: this list mixes coins, so each
          card carries the type dot, the coin icon and the coin name that the
          per-coin view has no need for. */}
      <div className="md:hidden flex flex-col gap-1.5">
        {grouped.map(({ tx, divider }) => (
          <React.Fragment key={tx.id}>
            {divider && (
              <div className="pt-3 pb-0.5">
                <MonthDivider label={divider} />
              </div>
            )}
            <div
              style={{
                background: 'rgb(var(--overlay) / 0.02)',
                border: '0.5px solid rgb(var(--overlay) / 0.06)',
                borderRadius: 10,
              }}
            >
              <TxnRow
                tx={tx}
                imageMap={imageMap}
                onEdit={onEdit}
                onDelete={onDelete}
                showAsset
              />
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* ── md and up: the table, unchanged ─────────────────────────────────── */}
      <div className="hidden md:block overflow-x-auto">
      <table className="w-full min-w-[760px]">
        <thead>
          <tr style={{ borderBottom: '0.5px solid rgb(var(--overlay) / 0.08)' }}>
            <th className="px-3 py-2.5 pl-0 text-left" style={TH}>Date</th>
            <th className="px-3 py-2.5 text-left" style={TH}>Type</th>
            <th className="px-3 py-2.5 text-left" style={TH}>Asset</th>
            <th className="px-3 py-2.5 text-right" style={TH}>Amount</th>
            <th className="px-3 py-2.5 text-right" style={TH}>Price</th>
            <th className="px-3 py-2.5 text-right" style={TH}>Total</th>
            <th className="px-3 py-2.5 text-left" style={TH}>Wallet</th>
            <th className="px-3 py-2.5 pr-0 text-right" style={TH}></th>
          </tr>
        </thead>
        <tbody>
          {grouped.map(({ tx, divider }) => {
            return (
              <React.Fragment key={tx.id}>
                {divider && (
                  <tr>
                    <td colSpan={8} className="pt-5 pb-1.5 pl-0">
                      <MonthDivider label={divider} />
                    </td>
                  </tr>
                )}
                <tr
                  className="group cursor-pointer transition-colors"
                  onClick={() => onEdit(tx)}
                  style={{ borderBottom: '0.5px solid rgb(var(--overlay) / 0.04)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'rgb(var(--overlay) / 0.025)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  {/* Date */}
                  <td className="px-3 py-3 pl-0 whitespace-nowrap">
                    <p className="text-[13px]" style={{ color: 'rgb(var(--sl-100))' }}>{formatDateShort(tx.date)}</p>
                    <p className="text-[10px] mt-0.5" style={{ color: 'var(--color-sub)' }}>{relativeTime(tx.date)}</p>
                  </td>

                  {/* Type */}
                  <td className="px-3 py-3"><TypePill type={tx.type} /></td>

                  {/* Asset */}
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2.5">
                      <CoinIcon coinId={tx.coinId} symbol={tx.symbol} imageMap={imageMap} size={28} />
                      <div className="min-w-0">
                        <p className="text-[13px] font-medium truncate" style={{ color: 'rgb(var(--sl-100))' }}>{tx.name}</p>
                        <p className="text-[10px] mt-0.5" style={{ color: 'var(--color-sub)' }}>{tx.symbol}</p>
                      </div>
                    </div>
                  </td>

                  {/* Amount */}
                  <td className="px-3 py-3 text-right num text-[13px]" style={{ color: 'rgb(var(--sl-100))' }}>
                    {formatAmount(tx.amount)}
                  </td>

                  {/* Price — user-entered, shown at the exact precision they logged */}
                  <td className="px-3 py-3 text-right num text-[13px]" style={{ color: 'var(--color-sub)' }}>
                    {tx.type === 'TRANSFER' ? '—' : formatExactPrice(txPriceInDisplay(tx, currency.code, rate).value, currency.code)}
                  </td>

                  {/* Total */}
                  <td className="px-3 py-3 text-right num text-[13px] font-medium" style={{ color: 'rgb(var(--sl-100))' }}>
                    <TotalCell tx={tx} />
                  </td>

                  {/* Wallet */}
                  <td className="px-3 py-3"><WalletCell tx={tx} /></td>

                  {/* Actions */}
                  <td className="px-3 py-3 pr-0 text-right">
                    <RowActions
                      onEdit={() => onEdit(tx)}
                      onDelete={() => onDelete(tx.id)}
                      deleteWarning={() => describeDeletionImpact(allTxs, tx.id)}
                    />
                  </td>
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      </div>
    </>
  );
}
